import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CSSProperties } from 'react'

export interface SearchableOption {
  value: string
  label: string
  hint?: string        // shown muted next to the label (e.g. clinic, "won't be notified")
  disabled?: boolean
  /** Greyed but still selectable (e.g. an Rx before the pharmacy's CareRx start date). */
  muted?: boolean
}

/**
 * A searchable single-select (combobox). Drop-in replacement for a <select> wherever the option list
 * can grow long enough that scrolling is painful. Type to filter; click or Enter to choose.
 * Keyboard: ArrowUp/Down to move, Enter to select, Escape to close.
 *
 * Project standard: data-bound dropdowns use this instead of a raw <select> (enforced by the
 * "searchable dropdowns" CI gate).
 */
export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  ariaLabel,
  emptyText = 'No matches',
  style,
  menuMinWidth,
}: {
  value: string
  onChange: (value: string) => void
  options: SearchableOption[]
  placeholder?: string
  ariaLabel?: string
  emptyText?: string
  style?: CSSProperties
  /** Opt-in: the open list is at least this wide (px) even when the field sits in a narrow column,
      kept inside the window. Omitted = the list matches the field, exactly as before. */
  menuMinWidth?: number
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  // ASP-57: the list renders in a body portal so ancestor overflow containers (scrolling
  // tables, drawers) can never clip it — the reported "search not showing the entire list".
  const [rect, setRect] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null)
  useLayoutEffect(() => {
    if (!open) { setRect(null); return }
    const place = () => {
      const el = rootRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - r.bottom
      const up = spaceBelow < 260 && r.top > 260
      const width = Math.max(r.width, menuMinWidth ?? 0)
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
      setRect({ top: up ? r.top : r.bottom, left: menuMinWidth ? left : r.left, width, up })
    }
    place()
    window.addEventListener('resize', place)
    // capture-phase scroll so scrolls inside nested containers reposition too
    window.addEventListener('scroll', place, true)
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true) }
  }, [open])

  const selected = options.find(o => o.value === value) || null

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(o => o.label.toLowerCase().includes(q) || (o.hint || '').toLowerCase().includes(q))
  }, [query, options])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (rootRef.current?.contains(t)) return
      if (listRef.current?.contains(t)) return   // clicks inside the portaled list stay open
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  useEffect(() => { if (open) setActive(0) }, [open, query])

  function choose(opt: SearchableOption) {
    if (opt.disabled) return
    onChange(opt.value)
    setOpen(false)
    setQuery('')
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, filtered.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) choose(filtered[active]) }
    else if (e.key === 'Escape') { setOpen(false) }
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', ...style }}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        value={open ? query : (selected?.label ?? '')}
        placeholder={selected ? selected.label : placeholder}
        title={selected?.label ?? ''}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        onKeyDown={onKeyDown}
        style={inputStyle}
      />
      <span aria-hidden="true" style={caret}>▾</span>
      {open && rect && createPortal(
        <ul ref={listRef} role="listbox"
            style={{ ...listStyle, position: 'fixed', zIndex: 9999, left: rect.left, width: rect.width,
                     ...(rect.up ? { bottom: window.innerHeight - rect.top + 3, top: 'auto' } : { top: rect.top + 3 }) }}>
          {filtered.length === 0 ? (
            <li style={{ ...optStyle, color: 'var(--color-text-mute,#7C766B)', cursor: 'default' }}>{emptyText}</li>
          ) : (
            filtered.map((o, i) => (
              <li
                key={o.value || `__${i}`}
                role="option"
                title={o.label}
                aria-selected={o.value === value}
                onMouseDown={e => { e.preventDefault(); choose(o) }}
                onMouseEnter={() => setActive(i)}
                style={{
                  ...optStyle,
                  background: i === active ? 'var(--color-primary-soft,#eff6ff)' : 'transparent',
                  color: o.disabled || o.muted ? 'var(--color-text-mute,#9aa)' : 'inherit',
                  fontStyle: o.muted && !o.disabled ? 'italic' : undefined,
                  cursor: o.disabled ? 'not-allowed' : 'pointer',
                  fontWeight: o.value === value ? 700 : 400,
                }}
              >
                <span>{o.label}</span>
                {o.hint && <span style={{ marginLeft: 8, fontSize: 11.5, color: 'var(--color-text-mute,#7C766B)' }}>{o.hint}</span>}
              </li>
            ))
          )}
        </ul>,
        document.body
      )}
    </div>
  )
}

const inputStyle: CSSProperties = { width: '100%', padding: '7px 26px 7px 9px', borderRadius: 6, border: '1px solid var(--color-border,#E8E3DA)', fontSize: 13, background: 'var(--color-bg,#fff)', boxSizing: 'border-box' }
const caret: CSSProperties = { position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 11, color: 'var(--color-text-mute,#7C766B)' }
const listStyle: CSSProperties = { maxHeight: 240, overflowY: 'auto', listStyle: 'none', padding: 4, margin: 0, background: 'var(--color-bg,#fff)', border: '1px solid var(--color-border,#E8E3DA)', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.12)' }
const optStyle: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 9px', borderRadius: 6, fontSize: 13 }
